import { useEffect, useMemo, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import { IconBuildingCommunity, IconCaretDown } from '@tabler/icons-react'
import { Tree, TreeDataNode, TreeProps } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { useRouter } from 'next/router'

import { BaseButton } from '../BaseButton'

import {
  InnerWrapper,
  LocationActions,
  LocationEmptyState,
  LocationLoading,
  Wrapper,
} from './ProjectLocationShared'
import { convertLocationsToTree, findNode } from './utils'

import { ButtonProps } from '@/components/shared-components/BUI/Button'
import { Popover } from '@/components/shared-components/BUI/Popover'
import { accountManagers } from '@/config/constants/roleGroups'
import { useHasRoles } from '@/hooks/useHasRoles'
import { GET_PROJECT_LOCATIONS_PUBLIC } from '@/modules/projects/graphQL'
import { decodeResourceLink } from '@/modules/resourceLink/coders'

import { buildPassTheme } from 'src/styles/theme'

import {
  ProjectLocationsPublicUiQuery,
  ProjectLocationsPublicUiQueryVariables,
} from 'types/api.graphql'
import { ProjectLocationLike } from 'types/vagueTypes'

export type ProjectLocationFormDropdownProps = {
  projectId: string
  value?: { id: string; projectLocationName: string }
  onChange?: (
    value: { id: string; projectLocationName: string } | null,
    node: TreeDataNode,
    includeSublocations: boolean
  ) => void
  children?: (node?: TreeDataNode) => React.ReactNode
  showIncludeSublocations?: boolean
  includeSublocations?: boolean
  buttonProps?: ButtonProps
  treeProps?: TreeProps
  showCaret?: boolean
  defaultLocations?: ProjectLocationLike[]
  required?: boolean
}

export const ProjectLocationFormDropdown: React.FC<
  ProjectLocationFormDropdownProps
> = ({
  projectId,
  value,
  onChange,
  children,
  showIncludeSublocations,
  includeSublocations = false,
  buttonProps,
  treeProps,
  showCaret = false,
  defaultLocations,
  required = false,
}) => {
  const previousProjectId = useRef(projectId)
  const { query } = useRouter()
  const canConfigureLocations = useHasRoles(accountManagers)

  const [open, toggleOpen] = useState(false)
  const slug = query.resourceLinkSlug
    ? decodeResourceLink(query.resourceLinkSlug as string).slug
    : null

  const { data, loading } = useQuery<
    ProjectLocationsPublicUiQuery,
    ProjectLocationsPublicUiQueryVariables
  >(GET_PROJECT_LOCATIONS_PUBLIC, {
    variables: { projectId, slug },
    skip: projectId === 'builder' || !projectId,
  })

  useEffect(() => {
    if (projectId !== previousProjectId.current) {
      onChange?.(null, null, includeSublocations)
      previousProjectId.current = projectId
    }
  }, [projectId, onChange, includeSublocations])

  const treeData = useMemo(() => {
    if (!data?.projectLocations_public && !defaultLocations) return []

    return convertLocationsToTree(
      defaultLocations || data?.projectLocations_public
    )
  }, [data?.projectLocations_public, defaultLocations])

  const selectedNode = useMemo(() => {
    if (!value?.id) return null
    return findNode(value.id, treeData)
  }, [value, treeData])

  const clear = () => {
    onChange?.(null, null, includeSublocations)
    toggleOpen(false)
  }

  const handleChangeIncludeSublocations = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked

    if (value?.id && selectedNode) {
      onChange?.(
        { id: value.id, projectLocationName: selectedNode.title as string },
        selectedNode,
        checked
      )
    } else {
      onChange?.(null, null, checked)
    }
  }

  const content = () => {
    if (loading) {
      return <LocationLoading loading={loading} />
    }

    if (!treeData.length) {
      return (
        <LocationEmptyState
          projectId={projectId}
          canConfigure={canConfigureLocations}
        />
      )
    }

    return (
      <Wrapper>
        <InnerWrapper>
          <Tree
            {...treeProps}
            showLine
            treeData={treeData}
            onSelect={(keys) => {
              if (keys.length > 0) {
                const id = keys[0] as string
                const node = findNode(id, treeData)
                onChange?.(
                  { id, projectLocationName: node.title as string },
                  node,
                  includeSublocations
                )
              } else {
                onChange?.(null, null, includeSublocations)
              }
            }}
            selectedKeys={value?.id ? [value.id] : []}
          />
        </InnerWrapper>
        <LocationActions
          showIncludeSublocations={showIncludeSublocations}
          includeSublocations={includeSublocations}
          hasValue={!!value}
          onChangeIncludeSublocations={handleChangeIncludeSublocations}
          onClear={clear}
        />
      </Wrapper>
    )
  }

  const showRequiredError = required && !value

  return (
    <Popover
      content={content()}
      trigger="click"
      placement="bottomLeft"
      showArrow={false}
      overlayStyle={{
        minWidth: '240px',
      }}
      open={open}
      onOpenChange={toggleOpen}
    >
      {children ? (
        children(selectedNode)
      ) : (
        <BaseButton
          hasValue={!!value}
          icon={<IconBuildingCommunity size={16} />}
          {...buttonProps}
          style={{
            border: showRequiredError
              ? `1px solid ${buildPassTheme.colour.semantic.errorDark}`
              : 'none',
            color: showRequiredError
              ? buildPassTheme.colour.semantic.errorDark
              : 'inherit',
          }}
        >
          {selectedNode?.title
            ? (selectedNode.title as React.ReactNode)
            : 'Add location'}

          {showCaret && (
            <IconCaretDown
              color={buildPassTheme.colour.secondary.base}
              fill={buildPassTheme.colour.secondary.base}
              size={16}
            />
          )}
        </BaseButton>
      )}
    </Popover>
  )
}
